import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader, StatusBadge, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Select } from "@/components/ui/form";
import { Card, CardBody, CardHeader, DataTable, DataTableHead, DataTableTd, DataTableTh } from "@/components/ui/card";
import { RfqSellerQuotesAddButton, RfqSellerQuotesSection } from "@/components/RfqSellerQuotesSection";
import { SectionAddButton } from "@/components/SectionAddButton";
import { RfqActionsMenu, type RfqActionItem } from "@/components/RfqActionsMenu";
import { RfqDocumentsAddButton, RfqDocumentsSection, type RfqAttachment } from "@/components/RfqDocumentsSection";
import { RfqEditSection } from "@/components/RfqEditSection";
import { RfqWorkflowStepper } from "@/components/RfqWorkflowStepper";
import { useAuth, canWrite } from "@/state/AuthContext";
import { apiFetch } from "@/utils/api";
import { openHtmlDocument } from "@/utils/attachments";

const emptyLine = {
  species: "",
  grade: "",
  dimensions: "",
  quantity: "",
  unit: "BF",
  description: "",
};

export function RfqDetailPage() {
  const { rfqId } = useParams();
  const { user, token } = useAuth();
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [sellers, setSellers] = useState<{ id: string; company_name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddSeller, setShowAddSeller] = useState(false);

  const [editingRfq, setEditingRfq] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [lineEdit, setLineEdit] = useState(emptyLine);

  const [showAddLine, setShowAddLine] = useState(false);
  const [showAddAttachment, setShowAddAttachment] = useState(false);

  const [newLine, setNewLine] = useState(emptyLine);
  const [addingLine, setAddingLine] = useState(false);
  const [savingLine, setSavingLine] = useState(false);
  const [deletingLine, setDeletingLine] = useState(false);

  const load = async () => {
    if (!rfqId) return;
    const [d, s] = await Promise.all([
      apiFetch<Record<string, unknown>>(`/rfqs/${rfqId}`),
      apiFetch<{ sellers: { id: string; company_name: string; email: string }[] }>("/sellers"),
    ]);
    setDetail(d);
    setSellers(s.sellers);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [rfqId]);

  if (loading) return <LoadingState />;
  if (error || !detail) return <ErrorState message={error || "Not found"} />;

  const rfq = detail.rfq as Record<string, unknown>;
  const lines = detail.lines as Record<string, unknown>[];
  const sellerQuotes = detail.sellerQuotes as Record<string, unknown>[];
  const purchaseOrder = detail.purchaseOrder as Record<string, unknown> | null;
  const attachments = detail.attachments as RfqAttachment[];
  const auditLog = detail.auditLog as Record<string, unknown>[];
  const quotesReadyForApproval = Boolean(detail.quotesReadyForApproval);
  const status = String(rfq.status);
  const write = canWrite(user);
  const isTerminal = status === "closed" || status === "cancelled";
  const canEdit = write && !isTerminal;

  const cancelRfq = async () => {
    if (!window.confirm("Cancel this RFQ? This cannot be undone.")) return;
    await apiFetch(`/rfqs/${rfqId}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    await load();
  };

  const addLine = async () => {
    setAddingLine(true);
    try {
      await apiFetch(`/rfqs/${rfqId}/lines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          species: newLine.species,
          grade: newLine.grade,
          dimensions: newLine.dimensions,
          quantity: Number(newLine.quantity) || 0,
          unit: newLine.unit,
          description: newLine.description,
        }),
      });
      setNewLine(emptyLine);
      setShowAddLine(false);
      await load();
    } finally {
      setAddingLine(false);
    }
  };

  const startEditLine = (line: Record<string, unknown>) => {
    setEditingLineId(String(line.id));
    setLineEdit({
      species: String(line.species ?? ""),
      grade: String(line.grade ?? ""),
      dimensions: String(line.dimensions ?? ""),
      quantity: String(line.quantity ?? ""),
      unit: String(line.unit ?? "BF"),
      description: String(line.description ?? ""),
    });
    setShowAddLine(false);
  };

  const saveLineEdit = async () => {
    if (!editingLineId) return;
    setSavingLine(true);
    try {
      await apiFetch(`/rfqs/${rfqId}/lines/${editingLineId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          species: lineEdit.species,
          grade: lineEdit.grade,
          dimensions: lineEdit.dimensions,
          quantity: Number(lineEdit.quantity) || 0,
          unit: lineEdit.unit,
          description: lineEdit.description,
        }),
      });
      setEditingLineId(null);
      await load();
    } finally {
      setSavingLine(false);
    }
  };

  const deleteLineEdit = async () => {
    if (!editingLineId) return;
    if (!window.confirm("Delete this line item? This cannot be undone.")) return;
    setDeletingLine(true);
    try {
      await apiFetch(`/rfqs/${rfqId}/lines/${editingLineId}`, { method: "DELETE" });
      setEditingLineId(null);
      await load();
    } finally {
      setDeletingLine(false);
    }
  };

  const transition = async (next: string) => {
    await apiFetch(`/rfqs/${rfqId}/transition`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    await load();
  };

  const issuePo = async () => {
    await apiFetch(`/rfqs/${rfqId}/po`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
    await load();
  };

  const mailtoRfq = () => {
    const emails = sellerQuotes
      .map((sq) => String(sq.seller_email ?? ""))
      .filter(Boolean)
      .join(",");
    const subject = encodeURIComponent(`RFQ ${rfq.rfq_number} — MWL Lumber`);
    const body = encodeURIComponent(`Please see attached RFQ ${rfq.rfq_number}.`);
    window.location.href = `mailto:${emails}?subject=${subject}&body=${body}`;
  };

  const uploadFile = async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entityType", "rfq");
    fd.append("entityId", rfqId!);
    fd.append("stage", status === "po_issued" ? "po" : "quote");
    await apiFetch("/attachments", { method: "POST", body: fd });
    setShowAddAttachment(false);
    await load();
  };

  const actionItems: RfqActionItem[] = [];
  if (canEdit) {
    if (status === "draft") {
      actionItems.push({ label: "Mark Sent to Sellers", onClick: () => void transition("sent_to_sellers") });
    }
    if (status === "sent_to_sellers") {
      actionItems.push({ label: "Mark Quotes Received", onClick: () => void transition("quotes_received") });
    }
    if (status === "quotes_received" && quotesReadyForApproval) {
      actionItems.push({ label: "Mark Customer Approved", onClick: () => void transition("customer_approved") });
    }
    if (status === "customer_approved" && !purchaseOrder) {
      actionItems.push({ label: "Issue PO", onClick: () => void issuePo() });
    }
    if (status === "po_issued") {
      actionItems.push({ label: "Close RFQ", onClick: () => void transition("closed") });
    }
    actionItems.push({
      label: editingRfq ? "Done Editing" : "Edit RFQ",
      onClick: () => {
        setEditingRfq(!editingRfq);
        setShowAddLine(false);
      },
    });
    actionItems.push({ label: "Cancel RFQ", onClick: () => void cancelRfq(), danger: true });
  }
  if (write) {
    actionItems.push({
      label: "View Seller Quote",
      onClick: () => void openHtmlDocument(`/rfqs/${rfqId}/seller-quote/print`, token),
    });
    if (sellerQuotes.length > 0) {
      actionItems.push({ label: "Email Sellers", onClick: mailtoRfq });
    }
    if (purchaseOrder) {
      actionItems.push({
        label: "Print PO",
        onClick: () => void openHtmlDocument(`/rfqs/${rfqId}/po/print`, token),
      });
    }
  }

  return (
    <div className="space-y-6">
      <Link to="/" className="inline-flex text-sm font-semibold text-argon-primary hover:underline mb-2">← Dashboard</Link>
      <PageHeader
        title={String(rfq.rfq_number)}
        subtitle={`${rfq.customer_name} · Requested by ${rfq.contact_name || "—"}`}
        actions={actionItems.length > 0 ? <RfqActionsMenu items={actionItems} /> : undefined}
      />

      <div className="flex flex-wrap gap-2 items-center">
        <StatusBadge status={status} />
        <span className="text-sm text-argon-secondary">Delivery: {String(rfq.requested_delivery_date || "—")}</span>
        {rfq.notes ? <span className="text-sm text-argon-secondary">Notes: {String(rfq.notes)}</span> : null}
        {rfq.winning_seller_name ? (
          <span className="text-sm text-argon-secondary">Seller: {String(rfq.winning_seller_name)}</span>
        ) : null}
      </div>

      {editingRfq && canEdit && (
        <RfqEditSection
          rfqId={rfqId!}
          rfq={rfq}
          onSaved={load}
          onCancel={() => setEditingRfq(false)}
        />
      )}

      <Card>
        <CardHeader title="Workflow" />
        <CardBody className="py-6">
          <RfqWorkflowStepper status={status} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Line Items"
          actions={
            canEdit ? (
              <SectionAddButton
                open={showAddLine}
                onClick={() => { setShowAddLine(!showAddLine); setEditingLineId(null); }}
              />
            ) : undefined
          }
        />
        <CardBody className="pt-2">
          <DataTable>
            <DataTableHead>
              <DataTableTh>#</DataTableTh>
              <DataTableTh>Species</DataTableTh>
              <DataTableTh>Grade</DataTableTh>
              <DataTableTh>Dimensions</DataTableTh>
              <DataTableTh>Qty</DataTableTh>
              <DataTableTh>Created By</DataTableTh>
              <DataTableTh>Unit Price</DataTableTh>
              <DataTableTh>Extended</DataTableTh>
              {canEdit && <DataTableTh>Actions</DataTableTh>}
            </DataTableHead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 9 : 8} className="px-4 py-8 text-center text-argon-secondary">
                    No line items yet.
                  </td>
                </tr>
              ) : (
                lines.map((l, index) => (
                  <tr key={String(l.id)} className="border-t border-gray-50">
                    <DataTableTd>{index + 1}</DataTableTd>
                    <DataTableTd>{String(l.species) || "—"}</DataTableTd>
                    <DataTableTd>{String(l.grade) || "—"}</DataTableTd>
                    <DataTableTd>{String(l.dimensions) || "—"}</DataTableTd>
                    <DataTableTd>{String(l.quantity)} {String(l.unit)}</DataTableTd>
                    <DataTableTd>{String(l.created_by_name || "—")}</DataTableTd>
                    <DataTableTd>{l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : "—"}</DataTableTd>
                    <DataTableTd>{l.extended_price != null ? `$${Number(l.extended_price).toFixed(2)}` : "—"}</DataTableTd>
                    {canEdit && (
                      <DataTableTd>
                        <Button type="button" variant="ghost" size="sm" onClick={() => startEditLine(l)}>Edit</Button>
                      </DataTableTd>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </DataTable>

          {editingLineId && canEdit && (
            <div className="mt-4 rounded-xl border border-argon-primary/20 bg-argon-primary/5 p-4 space-y-3">
              <p className="text-sm font-semibold text-argon-dark">Edit line item</p>
              <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <Input placeholder="Species" value={lineEdit.species} onChange={(e) => setLineEdit({ ...lineEdit, species: e.target.value })} />
                <Input placeholder="Grade" value={lineEdit.grade} onChange={(e) => setLineEdit({ ...lineEdit, grade: e.target.value })} />
                <Input placeholder="Dimensions" value={lineEdit.dimensions} onChange={(e) => setLineEdit({ ...lineEdit, dimensions: e.target.value })} />
                <Input type="number" placeholder="Qty" value={lineEdit.quantity} onChange={(e) => setLineEdit({ ...lineEdit, quantity: e.target.value })} />
                <Select value={lineEdit.unit} onChange={(e) => setLineEdit({ ...lineEdit, unit: e.target.value })}>
                  <option value="BF">BF</option>
                  <option value="MBF">MBF</option>
                  <option value="pcs">pcs</option>
                  <option value="lf">lf</option>
                </Select>
                <Input placeholder="Notes" value={lineEdit.description} onChange={(e) => setLineEdit({ ...lineEdit, description: e.target.value })} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={savingLine || deletingLine} onClick={saveLineEdit}>
                  {savingLine ? "Saving…" : "Save Line"}
                </Button>
                <Button type="button" variant="secondary" disabled={deletingLine} onClick={() => setEditingLineId(null)}>
                  Cancel
                </Button>
                <Button type="button" variant="danger" disabled={savingLine || deletingLine} onClick={deleteLineEdit}>
                  {deletingLine ? "Deleting…" : "Delete Line"}
                </Button>
              </div>
            </div>
          )}

          {showAddLine && canEdit && (
            <div className="mt-4 rounded-xl border border-gray-100 p-4 space-y-3 bg-gray-50/40">
              <p className="text-sm font-semibold text-argon-dark">Add line item</p>
              <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <Input placeholder="Species" value={newLine.species} onChange={(e) => setNewLine({ ...newLine, species: e.target.value })} />
                <Input placeholder="Grade" value={newLine.grade} onChange={(e) => setNewLine({ ...newLine, grade: e.target.value })} />
                <Input placeholder="Dimensions" value={newLine.dimensions} onChange={(e) => setNewLine({ ...newLine, dimensions: e.target.value })} />
                <Input type="number" placeholder="Qty" value={newLine.quantity} onChange={(e) => setNewLine({ ...newLine, quantity: e.target.value })} />
                <Select value={newLine.unit} onChange={(e) => setNewLine({ ...newLine, unit: e.target.value })}>
                  <option value="BF">BF</option>
                  <option value="MBF">MBF</option>
                  <option value="pcs">pcs</option>
                  <option value="lf">lf</option>
                </Select>
                <Input placeholder="Notes" value={newLine.description} onChange={(e) => setNewLine({ ...newLine, description: e.target.value })} />
              </div>
              <Button type="button" disabled={addingLine} onClick={addLine}>
                {addingLine ? "Adding…" : "Save Line"}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Documents"
          subtitle="Seller quote versions are saved automatically; upload other RFQ files here"
          actions={
            canEdit ? (
              <RfqDocumentsAddButton
                open={showAddAttachment}
                onClick={() => setShowAddAttachment(!showAddAttachment)}
              />
            ) : undefined
          }
        />
        <CardBody className="pt-2">
          <RfqDocumentsSection
            rfqId={rfqId!}
            rfqNumber={String(rfq.rfq_number)}
            attachments={attachments}
            token={token}
            canEdit={canEdit}
            showUpload={showAddAttachment}
            onUpload={uploadFile}
          />
        </CardBody>
      </Card>

      {canEdit && (
        <Card>
          <CardHeader
            title="Sellers & Quotes"
            subtitle="Upload each seller's quote and select the company that sent it"
            actions={
              <RfqSellerQuotesAddButton
                open={showAddSeller}
                onClick={() => setShowAddSeller(!showAddSeller)}
              />
            }
          />
          <CardBody>
            <RfqSellerQuotesSection
              rfqId={rfqId!}
              sellerQuotes={sellerQuotes}
              sellers={sellers}
              attachments={attachments}
              quotesReadyForApproval={quotesReadyForApproval}
              status={status}
              token={token}
              showAddSeller={showAddSeller}
              onToggleAddSeller={() => setShowAddSeller(false)}
              onRefresh={load}
            />
          </CardBody>
        </Card>
      )}

      {purchaseOrder && (
        <Card>
          <CardHeader title="Purchase Order" />
          <CardBody className="pt-2">
            <p className="text-sm text-argon-dark">{String(purchaseOrder.po_number)} · Total ${Number(purchaseOrder.total_amount ?? 0).toFixed(2)}</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Activity" />
        <CardBody className="pt-2">
          <ul className="text-sm space-y-2">
            {auditLog.map((a) => (
              <li key={String(a.id)} className="text-argon-secondary">
                {String(a.created_at)} — {String(a.user_name || "System")}: {String(a.action)} {a.detail ? `(${String(a.detail)})` : ""}
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
